import React, { createContext, useContext, useMemo, type ReactNode } from 'react'
import { type Flag } from '@fs/zion-flags'
import useCachedFeatureFlagWithPrefs from '../data-service/useCachedFeatureFlagWithPrefs'
import preferenceKeys from '../person-page/preference-keys'

export type CachedFlagResult<F extends Flag<object> = Flag<object>> = ReturnType<
  typeof useCachedFeatureFlagWithPrefs<F>
>

export type NewMergeAnalysisFlag = Flag<undefined> & {
  treatment: 'control' | 'newMergeV2' | 'off' | 'on'
}

type ControlResult = CachedFlagResult<Flag<undefined> & { treatment: 'control' }>

// Mirrors the zion-flags "control" treatment returned when flags are unavailable, so
// consumers rendered without the provider (unit tests) behave exactly as before.
const CONTROL_RESULT: ControlResult = {
  data: { treatment: 'control', config: undefined, isOn: false, isControl: true },
  loading: false,
  error: null,
}

const LABS_PREF = 'fslabs.labs'

const FLAGS = {
  contributionChangeLog: {
    cacheKey: 'tree-person.contributionChangeLog',
    flagName: 'treeWeb_personR9_contributionChangeLog',
    userPreferences: [LABS_PREF],
  },
  myHeritageHints: {
    cacheKey: 'tree-person.myHeritageHints',
    flagName: 'treeWeb_MyHeritageHints',
    userPreferences: [LABS_PREF],
  },
  newMergeAnalysis: {
    cacheKey: undefined,
    flagName: 'treeWeb_personR9_newMergeAnalysis',
    userPreferences: [LABS_PREF],
  },
  qualityIndicator: {
    cacheKey: preferenceKeys.QUALITY_INDICATOR,
    flagName: 'treeWeb_personR9_qualityIndicator',
    userPreferences: [LABS_PREF],
  },
  sourceCentricDetails: {
    cacheKey: 'tree-person.sourceCentricDetails',
    flagName: 'treeWeb_personR9_sourceCentricDetails',
    userPreferences: ['softLaunch.treeWeb_personR9_sourceCentricDetails', LABS_PREF],
  },
} as const

type FlagKey = keyof typeof FLAGS

// Each registered flag's shape. Registering a flag in FLAGS without an entry here (or without
// an evaluation in the provider below) is a compile error.
type FlagTypes = {
  contributionChangeLog: Flag<object>
  myHeritageHints: Flag<object>
  newMergeAnalysis: NewMergeAnalysisFlag
  qualityIndicator: Flag<object>
  sourceCentricDetails: Flag<object>
}

type CachedFlagsContextValue = { [K in FlagKey]: CachedFlagResult<FlagTypes[K]> }

// Every flag read can also come back as control: zion-flags returns the control treatment when
// the flag system is unavailable, and provider-less renders (unit tests) fall back to it here.
export type CachedFlag<K extends FlagKey> = CachedFlagsContextValue[K] | ControlResult

const CachedFlagsContext = createContext<CachedFlagsContextValue | undefined>(undefined)

type Props = {
  children: ReactNode
}

function useFlagEvaluation<F extends Flag<object> = Flag<object>>(flag: (typeof FLAGS)[FlagKey]): CachedFlagResult<F> {
  return useCachedFeatureFlagWithPrefs<F>(flag.flagName, flag.cacheKey, { userPreferences: [...flag.userPreferences] })
}

// Evaluates each registered flag exactly once for the whole app. Evaluating per consumer is
// measurably expensive (splitio matcher evaluation, preference subscription, and document.cookie
// parsing on every render), and some of these flags are read from dozens of components including
// per-card and per-family-member render paths.
export function CachedFlagsProvider({ children }: Props): JSX.Element {
  const contributionChangeLog = useFlagEvaluation(FLAGS.contributionChangeLog)
  const myHeritageHints = useFlagEvaluation(FLAGS.myHeritageHints)
  const newMergeAnalysis = useFlagEvaluation<NewMergeAnalysisFlag>(FLAGS.newMergeAnalysis)
  const qualityIndicator = useFlagEvaluation(FLAGS.qualityIndicator)
  const sourceCentricDetails = useFlagEvaluation(FLAGS.sourceCentricDetails)

  const value = useMemo(
    () => ({ contributionChangeLog, myHeritageHints, newMergeAnalysis, qualityIndicator, sourceCentricDetails }),
    [contributionChangeLog, myHeritageHints, newMergeAnalysis, qualityIndicator, sourceCentricDetails]
  )

  return <CachedFlagsContext.Provider value={value}>{children}</CachedFlagsContext.Provider>
}

// Reads one registered flag's app-wide evaluation.
export function useCachedFlag<K extends FlagKey>(flagKey: K): CachedFlag<K> {
  return useContext(CachedFlagsContext)?.[flagKey] ?? CONTROL_RESULT
}
